import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  MustWatchVideoNotification,
  MustWatchVideoTranslations,
} from '@skygroup/shared/components/must-watch-video/constants';
import {
  markVideoNotificationWatchedApi,
  useActiveVideoNotificationsApi,
} from '@skygroup/shared/components/must-watch-video/videoNotificationApi';
import { RootState } from '../store/store';

/**
 * Feeds `VideoNotificationsProvider` from GET /ms/video-notifications/active
 * and confirms videos through POST /ms/video-notifications/:id/watched.
 *
 * Only an agent working in agent mode receives them — regular site visitors
 * never fire the request.
 */
const useMustWatchVideoNotification = () => {
  const { agentInfo, translations } = useSelector(
    (state: RootState) => state.config
  );

  const enabled = !!agentInfo?.agent?.id && !!agentInfo?.agentMode;

  const { notifications, refetch } = useActiveVideoNotificationsApi({ enabled });

  const t = translations?.videoNotification;

  const mustWatchTranslations: MustWatchVideoTranslations = useMemo(
    () => ({
      title: t?.title ?? '',
      badge: t?.badge ?? '',
      optionalLabel: t?.optionalLabel ?? '',
      indicatorTooltip: t?.indicatorTooltip ?? '',
      blockedHint: t?.blockedHint ?? '',
      notificationsTitle: t?.notificationsTitle ?? '',
      newLabel: t?.newLabel ?? '',
      watchedLabel: t?.watchedLabel ?? '',
      seeMore: t?.seeMore ?? '',
      seeLess: t?.seeLess ?? '',
      emptyState: t?.emptyState ?? '',
      prevVideo: t?.prevVideo ?? '',
      nextVideo: t?.nextVideo ?? '',
      counterOf: t?.counterOf ?? '',
      done: t?.done ?? '',
      closeLabel: t?.closeLabel ?? '',
      videoUnavailable: t?.videoUnavailable ?? '',
    }),
    [t]
  );

  const handleDone = useCallback(
    async (watched: MustWatchVideoNotification) => {
      await markVideoNotificationWatchedApi(watched.id);
      // The provider flags the item locally straight away, so the list keeps
      // showing it in its watched state; the refetch just re-syncs the queue.
      refetch();
    },
    [refetch]
  );

  return {
    notifications,
    translations: mustWatchTranslations,
    handleDone,
    enabled,
  };
};

export default useMustWatchVideoNotification;
